import { z } from 'zod';
import { MOTION_LIBRARY } from '../presets/library.js';
/**
 * The accepted motion vocabulary, DERIVED from `MOTION_LIBRARY` at module load —
 * never a second hand-maintained copy of the same list.
 *
 * `schemas.ts` carries an Epic 34 note and dedicated drift-pin tests because two
 * hand-written copies of one vocabulary have already drifted apart in production
 * twice (`ai-gpt-edit`, `sharp-contain`). A retyped `z.enum([...])` here would be
 * the third. Deriving it means a motion added to the library is accepted by the
 * schema in the same commit, and a motion removed stops being accepted — with no
 * second edit to remember.
 *
 * This is also why the manifest's `motionId` is a CLOSED set rather than the
 * `z.string()` that `animationInstructionSchema.action` uses. The prompt path can
 * afford a loose action (a coder agent writes keyframes for anything); this path
 * cannot, because there is no coder — an unknown id has no geometry behind it.
 */
const MOTION_IDS = MOTION_LIBRARY.map((m) => m.id);
export const motionIdSchema = z.custom((value) => typeof value === 'string' && MOTION_IDS.includes(value), { message: 'motionId must name a motion the library carries' });
export const motionPlanEntrySchema = z.object({
    elementNumber: z.number().int().positive(),
    motionId: motionIdSchema,
    delayMs: z.number().min(0),
    durationMs: z.number().positive(),
});
export const skippedElementSchema = z.object({
    elementNumber: z.number().int().positive(),
    reason: z.string(),
});
/**
 * The planning model's entire output contract.
 *
 * `skipped` defaults to `[]` for the same live reason `animationInstructionSchema`
 * defaults `params`: a model that has nothing to report routinely omits the key,
 * and a manifest that animates everything is a valid manifest.
 */
export const motionManifestSchema = z.object({
    plan: z.array(motionPlanEntrySchema),
    skipped: z.array(skippedElementSchema).default([]),
});
